import React, { useState, useEffect } from "react";
import {
  NavLink,
} from "react-router-dom";
import { motion } from "framer-motion";
import logo from "@/assets/logo.png";
import { useTranslation } from "@/translations/TranslationContext";

// Material-UI imports
import { 
  Button, 
  MenuItem, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemButton,
  Box,
  Typography,
  Divider,
  Collapse,
  ListItemIcon,
  Popper,
  Grow,
  Paper,
  MenuList,
  ClickAwayListener,
  Select,
  FormControl,
  SelectChangeEvent
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PublicIcon from "@mui/icons-material/Public";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BookIcon from "@mui/icons-material/Book";
import CalculateIcon from "@mui/icons-material/Calculate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";

import "./Header.css";

const MobileNavItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  padding: '12px 16px',
  marginBottom: 8,
  backgroundColor: '#f8f9fa',
  '&:hover': {
    backgroundColor: '#e9ecef',
    transform: 'translateY(-2px)',
  },
  '&.active': {
    backgroundColor: '#0C4652',
    color: '#fff',
  },
}));

const StyledLanguageMenu = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
  overflow: 'hidden',
  width: 200,
  '& .MuiList-root': {
    padding: 0,
  },
}));

const LanguageMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&.selected': {
    backgroundColor: '#f0f7ff',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
  },
}));

const LanguageHeader = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #e9ecef',
}));

function Header() {
  const { language, setLanguage, t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const [servicesButtonRef, setServicesButtonRef] = useState<null | HTMLElement>(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  // Check if the screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close mobile menu
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Handle language menu
  const openLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const closeLanguageMenu = () => {
    setLanguageMenuAnchor(null);
  };

  // Handle language change
  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    closeLanguageMenu();
  };

  // Handle services menu
  const handleServicesButtonRef = (event: React.MouseEvent<HTMLElement>) => {
    setServicesButtonRef(event.currentTarget);
    setServicesMenuOpen(true);
  };

  const handleServicesMenuClose = () => {
    setServicesMenuOpen(false);
  };

  // Toggle mobile services submenu
  const toggleMobileServices = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  // Custom NavLink component
  const CustomNavLink = ({ to, children, onClick }: { to: string, children: React.ReactNode, onClick?: () => void }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive ? "nav-link active" : "nav-link"
      }
      onClick={onClick}
    >
      {children}
    </NavLink>
  );

  return (
    <div className="container">
      {isMobile ? (
        // Mobile layout with hamburger menu
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
            <motion.a
              href="/"
              className="logo"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <img src={logo} alt="Logo" style={{ width: 80 }} />
            </motion.a>
            
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleMobileMenu}
              sx={{ color: '#0C4652' }}
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          </Box>

          {/* Mobile Menu Drawer */}
          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={closeMobileMenu}
            PaperProps={{
              sx: {
                width: { xs: '85%', sm: 300 },
                padding: 3,
                backgroundColor: '#fff',
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'relative' }}>
                <img src={logo} alt="Logo" style={{ width: 80 }} />
                <IconButton
                  onClick={closeMobileMenu}
                  sx={{ position: 'absolute', right: 0, top: 0, color: '#0C4652' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <List>
              <ListItem disablePadding>
                <MobileNavItem
                  onClick={closeMobileMenu}
                >
                  <NavLink to="/" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                    <ListItemText primary={t.header.home} />
                  </NavLink>
                </MobileNavItem>
              </ListItem>
              <ListItem disablePadding>
                <MobileNavItem
                  onClick={closeMobileMenu}
                >
                  <NavLink to="/about" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                    <ListItemText primary={t.header.about} />
                  </NavLink>
                </MobileNavItem>
              </ListItem>
              
              {/* Services with submenu */}
              <ListItem disablePadding>
                <MobileNavItem onClick={toggleMobileServices}>
                  <ListItemText primary={t.header.services} />
                  {mobileServicesOpen ? <ExpandLess /> : <ExpandMore />}
                </MobileNavItem>
              </ListItem>
              <Collapse in={mobileServicesOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem disablePadding>
                    <MobileNavItem
                      onClick={closeMobileMenu}
                      sx={{ pl: 4, mb: 1 }}
                    >
                      <NavLink to="/services" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <ListItemText primary={t.header.allServices} />
                      </NavLink>
                    </MobileNavItem>
                  </ListItem>
                  <ListItem disablePadding>
                    <MobileNavItem
                      onClick={closeMobileMenu}
                      sx={{ pl: 4, mb: 1 }}
                    >
                      <NavLink to="/monthly-bookkeeping" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <BookIcon fontSize="small" sx={{ color: '#4CAF50' }} />
                        </ListItemIcon>
                        <ListItemText primary={t.header.monthlyBookkeeping} />
                      </NavLink>
                    </MobileNavItem>
                  </ListItem>
                  <ListItem disablePadding>
                    <MobileNavItem
                      onClick={closeMobileMenu}
                      sx={{ pl: 4, mb: 1 }}
                    >
                      <NavLink to="/tax-preparation" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <CalculateIcon fontSize="small" sx={{ color: '#F44336' }} />
                        </ListItemIcon>
                        <ListItemText primary={t.header.taxPreparation} />
                      </NavLink>
                    </MobileNavItem>
                  </ListItem>
                  <ListItem disablePadding>
                    <MobileNavItem
                      onClick={closeMobileMenu}
                      sx={{ pl: 4, mb: 1 }}
                    >
                      <NavLink to="/financial-planning" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <TrendingUpIcon fontSize="small" sx={{ color: '#2196F3' }} />
                        </ListItemIcon>
                        <ListItemText primary={t.header.financialPlanning} />
                      </NavLink>
                    </MobileNavItem>
                  </ListItem>
                  <ListItem disablePadding>
                    <MobileNavItem
                      onClick={closeMobileMenu}
                      sx={{ pl: 4, mb: 1 }}
                    >
                      <NavLink to="/business-consulting" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <BusinessIcon fontSize="small" sx={{ color: '#FF9800' }} />
                        </ListItemIcon>
                        <ListItemText primary={t.header.businessConsulting} />
                      </NavLink>
                    </MobileNavItem>
                  </ListItem>
                </List>
              </Collapse>
              
              <ListItem disablePadding>
                <MobileNavItem
                  onClick={closeMobileMenu}
                >
                  <NavLink to="/contact" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                    <ListItemText primary={t.header.contact} />
                  </NavLink>
                </MobileNavItem>
              </ListItem>
            </List>
            
            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Language Selector */}
              <FormControl fullWidth variant="outlined">
                <Select
                  value={language}
                  onChange={(e: SelectChangeEvent) => setLanguage(e.target.value)}
                  displayEmpty
                  startAdornment={
                    <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <PublicIcon sx={{ color: '#666', mr: 1 }} />
                    </Box>
                  }
                  sx={{
                    borderRadius: 30,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D9D9D9',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D9D9D9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0C4652',
                    },
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                      fontFamily: '"Montserrat", sans-serif',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 2,
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.15)',
                        mt: 1,
                      }
                    }
                  }}
                >
                  <MenuItem value="en">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      English
                      {language === 'en' && <CheckIcon sx={{ ml: 'auto', color: '#0C4652' }} />}
                    </Box>
                  </MenuItem>
                  <MenuItem value="fr">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Français
                      {language === 'fr' && <CheckIcon sx={{ ml: 'auto', color: '#0C4652' }} />}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#0C4652',
                  color: '#fff',
                  borderRadius: 30,
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1B9DB8',
                  },
                }}
                onClick={() => {
                  closeMobileMenu();
                  window.location.href = '/contact';
                }}
              >
                {t.header.scheduleConsultation}
              </Button>
            </Box>
          </Drawer>
        </>
      ) : (
        // Desktop layout
        <Box
          component="header"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2,
          }}
        >
          <motion.a
            href="/"
            className="logo d-flex align-items-center me-md-auto link-body-emphasis text-decoration-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{ marginRight: 'auto' }}
          >
            <img src={logo} alt="Logo" />
          </motion.a>

          <Box component="ul" className="nav">
            <Box component="li" className="nav-item">
              <CustomNavLink to="/">{t.header.home}</CustomNavLink>
            </Box>
            <Box component="li" className="nav-item">
              <CustomNavLink to="/about">{t.header.about}</CustomNavLink>
            </Box>
            <Box component="li" className="nav-item">
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  color: 'rgba(16, 24, 40, 0.6)',
                  '&:hover': {
                    color: 'rgba(27, 157, 184, 1)'
                  }
                }}
                onClick={handleServicesButtonRef}
                className="nav-link"
              >
                {t.header.services}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
              </Box>
              
              <Popper
                open={servicesMenuOpen}
                anchorEl={servicesButtonRef}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 1000 }}
              >
                {({ TransitionProps }) => (
                  <Grow
                    {...TransitionProps}
                    style={{ transformOrigin: 'top left' }}
                  >
                    <Paper elevation={3} sx={{ mt: 1, width: 310, boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)' }}>
                      <ClickAwayListener onClickAway={handleServicesMenuClose}>
                        <MenuList autoFocusItem={servicesMenuOpen}>
                          <MenuItem onClick={handleServicesMenuClose}>
                            <NavLink to="/services" style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}>
                              {t.header.allServices}
                            </NavLink>
                          </MenuItem>
                          <Divider />
                          <MenuItem onClick={handleServicesMenuClose}>
                            <NavLink to="/monthly-bookkeeping" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                              <BookIcon fontSize="small" sx={{ mr: 1.5, color: '#4CAF50' }} />
                              {t.header.monthlyBookkeeping}
                            </NavLink>
                          </MenuItem>
                          <MenuItem onClick={handleServicesMenuClose}>
                            <NavLink to="/tax-preparation" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                              <CalculateIcon fontSize="small" sx={{ mr: 1.5, color: '#F44336' }} />
                              {t.header.taxPreparation}
                            </NavLink>
                          </MenuItem>
                          <MenuItem onClick={handleServicesMenuClose}>
                            <NavLink to="/financial-planning" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                              <TrendingUpIcon fontSize="small" sx={{ mr: 1.5, color: '#2196F3' }} />
                              {t.header.financialPlanning}
                            </NavLink>
                          </MenuItem>
                          <MenuItem onClick={handleServicesMenuClose}>
                            <NavLink to="/business-consulting" style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}>
                              <BusinessIcon fontSize="small" sx={{ mr: 1.5, color: '#FF9800' }} />
                              {t.header.businessConsulting}
                            </NavLink>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Box>
            <Box component="li" className="nav-item">
              <CustomNavLink to="/contact">{t.header.contact}</CustomNavLink>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            {/* Language Selector Button */}
            <Button
              variant="outlined"
              startIcon={<PublicIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={openLanguageMenu}
              sx={{
                mr: 2,
                borderRadius: 30,
                padding: '8px 16px',
                textTransform: 'none',
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 500,
                borderColor: '#D9D9D9',
                color: '#333',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#D9D9D9',
                }
              }}
            >
              {language === 'en' ? 'English' : 'Français'}
            </Button>
            
            {/* Language Menu */}
            <Popper
              open={Boolean(languageMenuAnchor)}
              anchorEl={languageMenuAnchor}
              placement="bottom-start"
              transition
              disablePortal
              style={{ zIndex: 1000 }}
            >
              {({ TransitionProps }) => (
                <Grow
                  {...TransitionProps}
                  style={{ transformOrigin: 'top left' }}
                >
                  <StyledLanguageMenu>
                    <ClickAwayListener onClickAway={closeLanguageMenu}>
                      <Box>
                        <LanguageHeader>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                            {t.header.selectLanguage}
                          </Typography>
                        </LanguageHeader>
                        <MenuList>
                          <LanguageMenuItem 
                            onClick={() => handleLanguageChange('en')}
                            className={language === 'en' ? 'selected' : ''}
                          >
                            <ListItemText>English</ListItemText>
                            {language === 'en' && <CheckIcon sx={{ color: '#0C4652' }} />}
                          </LanguageMenuItem>
                          <LanguageMenuItem 
                            onClick={() => handleLanguageChange('fr')}
                            className={language === 'fr' ? 'selected' : ''}
                          >
                            <ListItemText>Français</ListItemText>
                            {language === 'fr' && <CheckIcon sx={{ color: '#0C4652' }} />}
                          </LanguageMenuItem>
                        </MenuList>
                      </Box>
                    </ClickAwayListener>
                  </StyledLanguageMenu>
                </Grow>
              )}
            </Popper>
            
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#0C4652',
                color: '#fff',
                borderRadius: 30,
                padding: '12px 24px',
                textTransform: 'none',
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#1B9DB8',
                },
              }}
              onClick={() => {
                window.location.href = '/contact';
              }}
            >
              {t.header.scheduleConsultation}
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
}

export default Header;
